const PERF_STORAGE_KEY = 'stockstars:perf'

/**
 * Structured performance logging for investigating ticker/search/dashboard slowness.
 *
 * Opt-in only (not enabled in dev by default):
 * - Runtime URL ?perf=1 (summary) or ?perf=verbose (per-request/chart detail)
 * - ?perf=0 clears persisted stockstars:perf from localStorage
 * - localStorage stockstars:perf=1 or stockstars:perf=verbose
 * - Build-time VITE_PERF_LOG=true
 *
 * Filter the browser console with "[PERF".
 */
/** Remove persisted perf logging (e.g. after ?perf=0). */
export function disablePerfLoggingPersistently() {
  if (typeof window === 'undefined') return

  try {
    localStorage.removeItem(PERF_STORAGE_KEY)
  }
  catch {
    // ignore
  }
}

/**
 * Apply URL-driven perf toggles before reading flags.
 * ?perf=0 (or false) clears localStorage so logging stays off after reload.
 */
export function syncRuntimePerfFlagFromUrl() {
  if (typeof window === 'undefined') return

  try {
    const params = new URLSearchParams(window.location.search)
    const perf = params.get('perf')
    if (perf === '0' || perf === 'false') {
      disablePerfLoggingPersistently()
    }
  }
  catch {
    // ignore
  }
}

export function readRuntimePerfFlag() {
  if (typeof window === 'undefined') return false

  syncRuntimePerfFlagFromUrl()

  try {
    const stored = localStorage.getItem(PERF_STORAGE_KEY)
    if (stored === '1' || stored === 'verbose') return true

    const params = new URLSearchParams(window.location.search)
    const perf = params.get('perf')
    if (perf === '1' || perf === 'verbose' || perf === '2') return true
    if (params.get('perfLog') === '1') return true
  }
  catch {
    // ignore private browsing / blocked storage
  }

  return false
}

export function readRuntimeVerbosePerfFlag() {
  if (typeof window === 'undefined') return false

  try {
    if (localStorage.getItem(PERF_STORAGE_KEY) === 'verbose') return true

    const params = new URLSearchParams(window.location.search)
    const perf = params.get('perf')
    if (perf === 'verbose' || perf === '2') return true
  }
  catch {
    // ignore private browsing / blocked storage
  }

  return false
}

export function isPerfLoggingEnabled() {
  return Boolean(
    import.meta.env.VITE_PERF_LOG === 'true' ||
      readRuntimePerfFlag(),
  )
}

export function isPerfLoggingVerbose() {
  return Boolean(
    import.meta.env.VITE_PERF_LOG === 'verbose' ||
      readRuntimeVerbosePerfFlag(),
  )
}

let perfLoggingInitialized = false

/** Persist perf logging across reloads (runtime toggle for prod/Docker builds). */
export function enablePerfLoggingPersistently(verbose = false) {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem(PERF_STORAGE_KEY, verbose ? 'verbose' : '1')
  }
  catch {
    // ignore
  }
}

/**
 * One-time startup hook — registers `stockstarsEnablePerfLog()` when perf is off.
 * When perf is on, prints a single console hint (summary vs verbose).
 */
export function initPerfLogging() {
  if (perfLoggingInitialized) return

  perfLoggingInitialized = true
  syncRuntimePerfFlagFromUrl()

  if (typeof window !== 'undefined') {
    window.stockstarsEnablePerfLog = (verbose = false) => {
      enablePerfLoggingPersistently(verbose)
      console.warn(
        `[PERF] Enabled (${verbose ? 'verbose' : 'summary'}). Reloading…`,
      )
      window.location.reload()
    }
  }

  if (!isPerfLoggingEnabled()) return

  console.warn(
    `[PERF] Performance logging ON (${
      isPerfLoggingVerbose() ? 'verbose' : 'summary'
    }). Filter console with "[PERF".`,
  )
}

export function approxJsonBytes(value) {
  if (value === undefined || value === null) return 0

  try {
    return new TextEncoder().encode(JSON.stringify(value)).length
  }
  catch {
    return 0
  }
}

/**
 * @param {string} category e.g. "ticker", "stockSearch", "coreInfo"
 * @param {string} event e.g. "getStockList", "filterComplete"
 * @param {Record<string, unknown>} [data]
 */
export function perfLog(category, event, data = {}) {
  if (!isPerfLoggingEnabled()) return
  if (category !== 'dashboard' && !isPerfLoggingVerbose()) return

  console.warn(`[PERF:${category}] ${event}`, {
    ...data,
    ts: new Date().toISOString(),
  })
}

/**
 * @param {string} category
 * @param {string} label
 * @returns {{ category: string, label: string, startMs: number } | null}
 */
export function perfStart(category, label) {
  if (!isPerfLoggingEnabled()) return null
  if (category !== 'dashboard' && !isPerfLoggingVerbose()) return null

  return {
    category,
    label,
    startMs: performance.now(),
  }
}

/**
 * @param {{ category: string, label: string, startMs: number } | null} handle
 * @param {Record<string, unknown>} [data]
 */
export function perfEnd(handle, data = {}) {
  if (!handle) return

  perfLog(handle.category, handle.label, {
    durationMs: Math.round((performance.now() - handle.startMs) * 100) / 100,
    ...data,
  })
}

let activeDashboardPerfSession = null

/**
 * Classify dashboard hydration HTTP requests for aggregate logging.
 * @param {string} url
 */
export function classifyDashboardRequestUrl(url) {
  if (url.includes('/api/v1/financial_reports?')) {
    return 'financial_reports_batch'
  }
  if (url.includes('/api/v1/financial_reports/')) {
    return 'financial_reports_single'
  }
  if (url.includes('/api/v1/dividends?')) return 'dividends_batch'
  if (url.includes('/api/v1/dividends/')) return 'dividends_single'
  if (url.includes('/api/v1/metrics/stock_metrics?')) {
    return 'stock_metrics_batch'
  }
  if (url.includes('/api/v1/metrics/stock_metrics/')) {
    return 'stock_metrics_single'
  }
  if (url.includes('/api/v1/metrics/dashboard_hydration?')) {
    return 'dashboard_hydration_batch'
  }
  if (url.includes('/api/v1/peers')) return 'peers'
  if (url.includes('/api/v1/fx_rates')) return 'fx_rates'
  if (url.includes('/api/v1/market/exchange_hours')) return 'exchange_hours'
  return 'other'
}

export function getActiveDashboardPerfSession() {
  return activeDashboardPerfSession
}

function initRequestCounts() {
  return {
    total: 0,
    byKind: {},
  }
}

function initCacheHitCounts() {
  return {
    total: 0,
    byPrefix: {},
  }
}

function bumpCount(container, key) {
  container.total += 1
  container.byKind[key] = (container.byKind[key] ?? 0) + 1
}

function bumpCachePrefix(container, cacheKey) {
  container.total += 1
  const prefix = String(cacheKey).split('_')[0]
  container.byPrefix[prefix] = (container.byPrefix[prefix] ?? 0) + 1
}

/**
 * Start a dashboard tab hydration perf session (`[PERF:dashboard]`).
 * @param {Record<string, unknown>} meta
 */
export function startDashboardTabHydrationPerf(meta = {}) {
  if (!isPerfLoggingEnabled()) return null

  const session = {
    meta,
    startMs: performance.now(),
    phases: {},
    charts: [],
    requests: initRequestCounts(),
    cacheHits: initCacheHitCounts(),
  }

  activeDashboardPerfSession = session
  perfLog('dashboard', 'tabHydrationStart', meta)
  return session
}

export function recordDashboardPhase(session, phase, startMs, extra = {}) {
  if (!session) return

  session.phases[phase] = {
    durationMs: Math.round((performance.now() - startMs) * 100) / 100,
    ...extra,
  }
}

export function recordDashboardHttpRequest(session, url) {
  if (!session) return

  const kind = classifyDashboardRequestUrl(url)
  bumpCount(session.requests, kind)

  if (isPerfLoggingVerbose()) {
    perfLog('dashboard', 'httpRequest', { kind, url })
  }
}

export function recordDashboardCacheHit(session, cacheKey) {
  if (!session) return

  bumpCachePrefix(session.cacheHits, cacheKey)

  if (isPerfLoggingVerbose()) {
    perfLog('dashboard', 'cacheHit', { cacheKey })
  }
}

export function recordDashboardChartHydration(session, chartMeta, durationMs) {
  if (!session) return

  const entry = {
    ...chartMeta,
    durationMs: Math.round(durationMs * 100) / 100,
  }
  session.charts.push(entry)

  if (isPerfLoggingVerbose()) {
    perfLog('dashboard', 'chartHydrated', entry)
  }
}

/**
 * Wrap tab hydration to count network requests while perf logging is enabled.
 */
export async function withDashboardRequestTracking(
  requestManager,
  session,
  asyncFn,
) {
  if (!session || !requestManager?.doGetRequest) {
    return asyncFn()
  }

  const originalDoGetRequest = requestManager.doGetRequest.bind(requestManager)

  requestManager.doGetRequest = async (url) => {
    recordDashboardHttpRequest(session, url)
    return originalDoGetRequest(url)
  }

  try {
    return await asyncFn()
  }
  finally {
    requestManager.doGetRequest = originalDoGetRequest
  }
}

/**
 * Finish a dashboard tab hydration session and emit the summary span.
 * @param {ReturnType<typeof startDashboardTabHydrationPerf>} session
 * @param {Record<string, unknown>} [extra]
 */
export function endDashboardTabHydrationPerf(session, extra = {}) {
  if (!session) return

  if (activeDashboardPerfSession === session) {
    activeDashboardPerfSession = null
  }

  const financialReportsBatch =
    session.requests.byKind.financial_reports_batch ?? 0
  const financialReportsSingle =
    session.requests.byKind.financial_reports_single ?? 0
  const dividendsBatch = session.requests.byKind.dividends_batch ?? 0
  const dividendsSingle = session.requests.byKind.dividends_single ?? 0

  perfLog('dashboard', 'tabHydrationComplete', {
    ...session.meta,
    ...extra,
    durationMs: Math.round((performance.now() - session.startMs) * 100) / 100,
    phases: session.phases,
    chartCount: session.charts.length,
    ...(isPerfLoggingVerbose() ? { charts: session.charts } : {}),
    requests: session.requests,
    cacheHits: session.cacheHits,
    financialReportsRequestCount: financialReportsBatch +
      financialReportsSingle,
    financialReportsBatchRequestCount: financialReportsBatch,
    dividendsRequestCount: dividendsBatch + dividendsSingle,
    dividendsBatchRequestCount: dividendsBatch,
  })
}

export const testables = {
  bumpCachePrefix,
  bumpCount,
  classifyDashboardRequestUrl,
}
